import React from 'react';
import { useHistory } from 'react-router-dom';
import {
  FormControl,
  Select,
  MenuItem,
  makeStyles,
} from '@material-ui/core';
import { useOrganizationStore } from '../../store/organization.store';
import OrgLogoChip from '../OrgLogoChip';
import * as ROUTES from '../../constants/routes';

const useStyles = makeStyles((theme) => ({
  formControl: {
    minWidth: 180,
    marginRight: 15,
  },
  select: {
    backgroundColor: 'transparent',
    color: '#fff',
    fontFamily: 'var(--font-family-primary)',
    fontSize: 'var(--menu-font-size)',
    padding: '4px 8px',
    '& .MuiSelect-select': {
      // MUI's dropdown-arrow icon is absolute-positioned at right: 7px and
      // is ~24px wide, so it occupies roughly 7-31px from the right edge.
      // paddingRight must clear that entire footprint or long org names
      // (especially when the switcher shrinks in the mobile drawer) will
      // sit under the arrow. 32px gives a small buffer beyond the icon.
      paddingRight: '32px',
      '&:focus': {
        backgroundColor: 'transparent',
      },
    },
    '& .MuiSelect-icon': {
      color: '#fff',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.1)',
      top: 0,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.2)',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.2)',
      borderWidth: '1px',
    },
  },
  menuPaper: {
    backgroundColor: '#1e1e2f',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: 4,
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
  },
  menuItem: {
    color: 'rgba(255, 255, 255, 0.9)',
    fontFamily: 'var(--font-family-primary)',
    fontSize: 'var(--menu-font-size)',
    padding: '10px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
    },
    '& i': {
      fontSize: '14px',
      width: '20px',
    },
  },
  loadingText: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: 'var(--menu-font-size)',
    fontFamily: 'var(--font-family-primary)',
    padding: '6px 12px',
  },
}));

/**
 * Organization Switcher Component
 *
 * Displays the current organization name with a dropdown for Switch and Setup options.
 * Switch navigates to the select organization page.
 * Setup navigates to the system admin page (admin only).
 */
const OrganizationSwitcher: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();

  // Organization store state
  const currentOrgName = useOrganizationStore(state => state.currentOrgName);
  const currentOrgRole = useOrganizationStore(state => state.currentOrgRole);
  const organizations = useOrganizationStore(state => state.organizations);
  const isSuperAdmin = useOrganizationStore(state => state.isSuperAdmin);
  const isLoading = useOrganizationStore(state => state.isLoading);

  // Don't render if user has no organizations and is not a super admin
  if (!isSuperAdmin && organizations.length === 0) {
    return null;
  }

  // Show loading state
  if (isLoading) {
    return (
      <div className={classes.loadingText}>Loading...</div>
    );
  }

  const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const value = event.target.value as string;
    if (value === 'switch') {
      history.push(ROUTES.SELECT_ORGANIZATION);
    } else if (value === 'setup') {
      history.push(ROUTES.ADMIN);
    }
  };

  // Check if user is admin (can access Setup)
  const isAdmin = isSuperAdmin || currentOrgRole === 'ADMIN';

  return (
    <FormControl variant="outlined" className={classes.formControl} size="small">
      <Select
        value=""
        onChange={handleChange}
        displayEmpty
        className={classes.select}
        renderValue={() => (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              minWidth: 0, // allow the inner text span to shrink for truncation
              maxWidth: '100%',
            }}
          >
            <OrgLogoChip size={40} />
            <span
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
              }}
            >
              {currentOrgName || 'Select Organization'}
            </span>
          </span>
        )}
        MenuProps={{
          classes: { paper: classes.menuPaper },
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'right',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'right',
          },
          getContentAnchorEl: null,
        }}
      >
        <MenuItem value="switch" className={classes.menuItem}>
          <i className="tim-icons icon-refresh-02" />
          <span>Switch</span>
        </MenuItem>
        {isAdmin && (
          <MenuItem value="setup" className={classes.menuItem}>
            <i className="tim-icons icon-settings-gear-63" />
            <span>Setup</span>
          </MenuItem>
        )}
      </Select>
    </FormControl>
  );
};

export default OrganizationSwitcher;
